import React, { useRef, useState, useEffect } from 'react';
import ReactGA from "react-ga4";
import emailjs from '@emailjs/browser';
import { contentData } from '../data/contentData';
import PageTopBanner from "../components/PageTopBanner";
import ReassuranceBanner from '../components/ReassuranceBanner';
import Modal from '../components/Modal';
import Footer from "../components/Footer";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import ReCAPTCHA from 'react-google-recaptcha';
import "../style/pages/_candidater.scss";
import "../style/components/_modal.scss";

// Schéma de validation avec Zod
const candidaterSchema = z.object({
    user_name: z
        .string()
        .min(1, 'Le nom est requis')
        .min(2, 'Le nom doit contenir au moins 2 caractères'),
    user_email: z
        .string()
        .min(1, 'L\'email est requis')
        .email('Format d\'email invalide'),
    user_phone: z
        .string()
        .min(1, 'Le numéro de téléphone est requis')
        .regex(/^0[1-9]\d{8}$/, 'Format français requis (ex: 0612345678)'),
    message: z
        .string()
        .min(1, 'Le message est requis')
        .min(10, 'Le message doit contenir au moins 10 caractères')
});

const Candidater = () => {
    useEffect(() => {
        ReactGA.send({
            hitType: "pageview",
            page: "/candidater",
            title: "Candidater",
        });
    });

    const form = useRef();
    const recaptchaRef = useRef();
    const [status, setStatus] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isError, setIsError] = useState(false);
    const [recaptchaError, setRecaptchaError] = useState('');

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset
    } = useForm({
        resolver: zodResolver(candidaterSchema),
        mode: 'onChange'
    });

    const closeModal = () => {
        setIsModalOpen(false);
        if (!isError) {
            reset();
            recaptchaRef.current.reset();
        }
    };

    const trackApplicationFormSubmission = (success) => {
        ReactGA.event({
            category: "Form Interaction",
            action: "Formulaire de Candidature Envoyé",
            label: success ? "Succès" : "Echec"
        });
    };

    const candidaterData = contentData.find((content) => content.id === 2);
    if (!candidaterData) {
        return <div>Pas de données pour le contenu demandé</div>
    }

    const onSubmit = async (data) => {
        const recaptchaValue = recaptchaRef.current.getValue();
        if (!recaptchaValue) {
            setRecaptchaError('Veuillez valider le reCAPTCHA');
            return;
        }
        setRecaptchaError('');

        try {
            await emailjs.sendForm('contact_formulaire', 'contact_form', form.current, {
                publicKey: 'MEcqQdvw3O3ydkCSS',
            });

            setStatus('Votre message a été envoyé avec succès.');
            setIsError(false);
            setIsModalOpen(true);
            trackApplicationFormSubmission(true);
        } catch (error) {
            setStatus(`Erreur lors de l'envoi : ${error.text}`);
            setIsError(true);
            setIsModalOpen(true);
            trackApplicationFormSubmission(false);
        }
    };

    return (
        <div>
            <main>
                <PageTopBanner
                    img={candidaterData.img}
                    title={candidaterData.title}
                    info={candidaterData.info}
                />
                <div className="o3academie-container">
                    <div className="container" key={candidaterData.id}>
                        <h2>Candidater, comment ça marche ?</h2>
                        <p className="apply-info_text">{candidaterData.shortDescription}</p>

                        <div className="steps-container">
                            {candidaterData.steps.map((step) => (
                                <div className="steps-section">
                                    {step.icon}
                                    <h3>{step.title}</h3>
                                    <div className="vertical-bar"></div>

                                    <ul>
                                        {Array.isArray(step.description) ? (step.description.map((descriptif) => (
                                            <li>{descriptif}</li>
                                        ))
                                        ) : (
                                            <li>{step.description}</li>
                                        )}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="contact-banner">
                    <div className="contact-title">
                        <h2>Nous Contacter</h2>
                    </div>
                    <form ref={form} onSubmit={handleSubmit(onSubmit)} className="contact-form">
                        <div className="form-container">
                            <div className="info-user_container">
                                <div className="form-item">
                                    <label htmlFor="name">Votre nom</label>
                                    <input
                                        id="name"
                                        type="text"
                                        className={errors.user_name ? 'error' : ''}
                                        {...register('user_name')}
                                    />
                                    {errors.user_name && (
                                        <span className="error-message">{errors.user_name.message}</span>
                                    )}
                                </div>
                                <div className="form-item">
                                    <label htmlFor="mail">Votre e-mail</label>
                                    <input
                                        id="mail"
                                        type="email"
                                        className={errors.user_email ? 'error' : ''}
                                        {...register('user_email')}
                                    />
                                    {errors.user_email && (
                                        <span className="error-message">{errors.user_email.message}</span>
                                    )}
                                </div>
                                <div className="form-item">
                                    <label htmlFor="numero">Votre numéro de téléphone</label>
                                    <input
                                        id="numero"
                                        type="tel"
                                        className={errors.user_phone ? 'error' : ''}
                                        {...register('user_phone')}
                                    />
                                    {errors.user_phone && (
                                        <span className="error-message">{errors.user_phone.message}</span>
                                    )}
                                </div>
                            </div>
                            <div className="msg-container">
                                <div className="form-item">
                                    <label htmlFor="msg">Votre message</label>
                                    <textarea
                                        id="msg"
                                        className={errors.message ? 'error' : ''}
                                        {...register('message')}
                                    />
                                    {errors.message && (
                                        <span className="error-message">{errors.message.message}</span>
                                    )}
                                </div>
                                <div className="recaptcha-container">
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey="6LeDCHMrAAAAAL5uCQsKNsVQwESU-bi8aogYFcxU"
                                        theme="light"
                                        onChange={(value) => {
                                            if (value) setRecaptchaError('');
                                        }}
                                        onExpired={() => {
                                            setRecaptchaError('Le reCAPTCHA a expiré, veuillez le refaire');
                                        }}
                                        onError={() => {
                                            setRecaptchaError('Erreur reCAPTCHA, veuillez réessayer');
                                        }}
                                    />
                                    {recaptchaError && (
                                        <span className="error-message">{recaptchaError}</span>
                                    )}
                                </div>
                                <input
                                    type="submit"
                                    value={isSubmitting ? "Envoi..." : "Envoyer"}
                                    className="submit-btn"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                    </form>
                    <Modal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        message={status}
                        isError={isError}
                    />
                </div>
                <ReassuranceBanner />
            </main>
        </div>
    );
};

export default Candidater;